import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { throttle } from '../../../utilities/throttle';

type StickyValue = 'sticky' | '-webkit-sticky';
interface ItemProps {
  children: React.ReactNode;
  stickyValue?: StickyValue;
  $stickyTop?: string | number;
  $fixedTop?: number;
  $zIndex?: number | 'auto';
  $forceFixed?: boolean;
}

interface ContainerProps {
  children: React.ReactNode;
  $forceFixed?: boolean;
}

const RefContext = createContext<RefObject<HTMLDivElement> | null>(null);

/**
 * This function detects if 'sticky' is supported
 * if the sticky value isn't supported it won't assign it
 * Returns sticky with prefix or 'fixed'
 */
const getStickyOrFixed = () => {
  const test = document.head.style;

  ['', '-o-', '-webkit-', '-moz-', '-ms-'].forEach((prefix) => {
    test.position = `${prefix}sticky`;
  });

  const stickyPositionProperty = test.position || 'fixed';

  test.position = '';

  return stickyPositionProperty;
};

export const ContainerFixed = ({ children }: ContainerProps) => {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <RefContext.Provider value={ref}>
      <div ref={ref}>{children}</div>
    </RefContext.Provider>
  );
};

export const ItemFixed = ({
  children,
  $zIndex: zIndex,
  $fixedTop: fixedTop = 0,
}: ItemProps) => {
  const parentRef = useContext(RefContext);
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);
  const [style, setStyle] = useState({});

  useEffect(() => {
    if (ref.current === null) return;
    const itemHeight = ref.current.offsetHeight;
    if (itemHeight !== height) setHeight(itemHeight);

    const checkPosition = throttle(() => {
      // Cannot use getBoundingClientRect because IE does not provide X and Y
      if (ref.current === null || !parentRef?.current) {
        return;
      }
      const itemShape = {
        width: ref.current.offsetWidth,
        height: ref.current.offsetHeight,
        y: ref.current.offsetTop - window.pageYOffset,
      };
      const parentShape = {
        height: parentRef.current.offsetHeight,
        y: parentRef.current.offsetTop - window.pageYOffset,
      };

      if (itemShape.y < 0) {
        const diff = parentShape.y + parentShape.height - itemShape.height;
        const top =
          diff > 0
            ? 0 + fixedTop
            : diff < 0 - height
              ? 0 - height + fixedTop
              : diff + fixedTop;

        setStyle({
          position: 'fixed',
          top,
          width: itemShape.width,
          zIndex,
        });
      } else {
        setStyle({});
      }
    }, 10);

    checkPosition();

    window.addEventListener('scroll', checkPosition);

    return () => {
      window.removeEventListener('scroll', checkPosition);
    };
  }, [fixedTop, height, parentRef, zIndex]);

  return (
    <div ref={ref} style={{ height }}>
      <div style={style}>{children}</div>
    </div>
  );
};

const ContainerSticky = ({ children }: ContainerProps) => <div>{children}</div>;

const ItemSticky = ({
  children,
  $zIndex: zIndex = 'auto',
  $stickyTop: stickyTop = 0,
  stickyValue,
}: ItemProps) => (
  <div style={{ position: stickyValue, top: stickyTop, zIndex }}>
    {children}
  </div>
);

export const Container = (props: ContainerProps) => {
  const [positionProperty, setPositionProperty] = useState('sticky');

  useEffect(() => {
    setPositionProperty(getStickyOrFixed());
  }, []);

  if (props.$forceFixed || positionProperty === 'fixed') {
    return <ContainerFixed {...props} />;
  }

  return <ContainerSticky {...props} />;
};

export const Item = (props: ItemProps) => {
  const [positionProperty, setPositionProperty] = useState('sticky');

  useEffect(() => {
    setPositionProperty(getStickyOrFixed());
  }, []);

  if (props.$forceFixed || positionProperty === 'fixed') {
    return <ItemFixed {...props} />;
  }

  return (
    <ItemSticky stickyValue={positionProperty as StickyValue} {...props} />
  );
};

/** # Sticky (Legacy Browsers)
This Component is needed to support position Sticky
in old browsers such us <IE11.
If position sticky is supported, then it uses it,
otherwise uses listeners and calculates distances
to simulate the sticky functionality in JS.

It requires 2 components, `Sticky.Container` and `Sticky.Item`.

*/
export const Sticky = { Container, ContainerFixed, Item, ItemFixed };
